import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export class SkidPdfReport {
    /**
     * Generates and downloads a PDF report.
     * @param {Object} data - The data object.
     */
    static exportToPdf(data: any) {
        const doc = new jsPDF();
        const title = "Grouting SKID Report";
        const { worksite, machine } = data;
        let createFilename = new Date().toLocaleDateString() + '_' + machine.id + '_' + (worksite.projectName || 'Project') + '_' + (worksite.worksiteName || 'Worksite') + '_' + (worksite.tunnelName || 'Tunnel') + '_' + (worksite.peg || 'Peg') + '_' + (worksite.screen || 'Screen');
        doc.setFontSize(18);
        doc.text(title, 14, 22);
        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 30);

        let finalY = 35;
        let isFirstSection = true;

        data.selectedSections.forEach((report: string) => {
            if (!isFirstSection) {
                doc.addPage();
                finalY = 20; // Reset Y for new page
            }

            switch (report) {
                case 'Frontpage': finalY = this.AddFrontpageSection(data, doc, finalY); break;
                case 'Summary': finalY = this.AddSummarySection(data, doc, finalY); break;
                case 'Setup':  finalY = this.AddSetupSection(data, doc, finalY); break;
                case 'PumpInformation': finalY = this.AddPumpInformationSection(data, doc, finalY); break;
                case 'PumpRecipeGantt': finalY = this.AddPumpRecipeGanttSection(data, doc, finalY); break;
                case 'Status': finalY = this.AddStatusSection(data, doc, finalY); break;
                case 'Material': finalY = this.AddMaterialSection(data, doc, finalY); break;
                case 'RecipeInformation': finalY = this.AddRecipeInformationSection(data, doc, finalY); break;
                case 'GeoAnalysis': finalY = this.AddGeoAnalysisSection(data, doc, finalY); break;
                case 'HoleGraphs': finalY = this.AddHoleGraphsSection(data, doc, finalY); break;
                case 'Events':  finalY = this.AddEventsSection(data, doc, finalY); break;
                default: break;
            }
            isFirstSection = false;
        });
        doc.save(createFilename + '.pdf');
    }

    static AddHeaderSubtext(data: any, doc: jsPDF, finalY: number) {
        const worksite = data.worksite;
        const machine = data.machine;
        const timeInformation = data.timeInformation;
        const parts0 = [];
        const parts1 = [];
        if (machine && machine.id) {
            parts0.push(`Machine: ${machine.id}`);
        }
        if (worksite) {
            if (worksite.projectName) parts0.push(worksite.projectName);
            if (worksite.tunnelName) parts0.push(`Tunnel: ${worksite.tunnelName}`);
            if (worksite.peg) parts0.push(`Peg: ${worksite.peg}`);
            if (worksite.screen) parts0.push(`Screen: ${worksite.screen}`);
        }
        if (timeInformation) {
            parts1.push(`Start: ${timeInformation.startTime}`);
            parts1.push(`Stop: ${timeInformation.stopTime}`);
            parts1.push(`Duration: ${timeInformation.totalTime}`);
        }
        if (parts0.length > 0 && parts1.length > 0) {
            doc.setFontSize(9);
            doc.setTextColor(100);
            doc.text(parts0.join(' | '), 20, finalY);
            doc.text(parts1.join(' | '), 20, finalY + 5);
            doc.setTextColor(0);
            doc.setFontSize(11);
            return finalY + 10;
        }
        return finalY;
    }

    static AddFrontpageSection(data: any, doc: jsPDF, finalY: number) {
        const worksite = data.worksite;
        const machine = data.machine;
        const timeInformation = data.timeInformation;
        const totalUsedMaterialFromEvents = data.totalUsedMaterialFromEvents;
        const projectImage = data.projectImage;
        const projectAdditionalData = data.projectAdditionalData;
        if (worksite || machine || timeInformation) {
            doc.setFontSize(14);
            doc.text("General Information", 14, finalY + 10);
            finalY += 20;
            const tableBody = [];
            if (worksite) {
                tableBody.push([{ content: 'Project Information', colSpan: 2, styles: { halign: 'center', fillColor: [96, 125, 139], textColor: [255, 255, 255], fontStyle: 'bold' } }]);
                tableBody.push(['Project Name', worksite.projectName || '-']);
                tableBody.push(['Worksite', worksite.worksiteName || '-']);
                tableBody.push(['Tunnel', worksite.tunnelName || '-']);
                tableBody.push(['Peg', worksite.peg || '-']);
                tableBody.push(['Screen', worksite.screen || '-']);
            }
            if (machine) {
                tableBody.push([{ content: 'Machine Information', colSpan: 2, styles: { halign: 'center', fillColor: [96, 125, 139], textColor: [255, 255, 255], fontStyle: 'bold' } }]);
                tableBody.push(['Machine ID', machine.id || '-']);
                tableBody.push(['Machine Type', machine.type || '-']);
            }
            if (timeInformation) {
                tableBody.push([{ content: 'Production Time', colSpan: 2, styles: { halign: 'center', fillColor: [96, 125, 139], textColor: [255, 255, 255], fontStyle: 'bold' } }]);
                tableBody.push(['Start Time', timeInformation.startTime ? timeInformation.startTime : '-']);
                tableBody.push(['Stop Time', timeInformation.stopTime ? timeInformation.stopTime: '-']);
                tableBody.push(['Total Duration', timeInformation.totalTime || '-']);
            }

            autoTable(doc, {
                startY: finalY,
                body: tableBody,
                theme: 'grid',
                styles: { fontSize: 10 },
                columnStyles: {
                    0: { fontStyle: 'bold', cellWidth: 50 },
                    1: { cellWidth: 'auto' }
                }
            });

            finalY = (doc as any).lastAutoTable.finalY;
            finalY += 10;
        }

        if (totalUsedMaterialFromEvents && totalUsedMaterialFromEvents.length > 0) {
            doc.setFontSize(14);

            const renderTable = (title: string, model: any) => {
                if (!model) return;

                if (finalY > 240) {
                    doc.addPage();
                    finalY = 20;
                }

                doc.setFontSize(11);
                doc.text(title, 14, finalY);

                const body: any[] = [];
                const pushIfVal = (label: string, val: number, unit: string) => {
                    if (val > 0) body.push([label, val.toFixed(2) + ' ' + unit]);
                };

                pushIfVal('Cement 1', model.cement1, 'kg');
                pushIfVal('Cement 2', model.cement2, 'kg');
                pushIfVal('Additive 1', model.additive1, 'L');
                pushIfVal('Additive 2', model.additive2, 'L');
                pushIfVal('Additive 3', model.additive3, 'L');
                pushIfVal('Additive 4', model.additive4, 'L');
                pushIfVal('Additive 5', model.additive5, 'L');
                pushIfVal('Additive 6', model.additive6, 'L');
                pushIfVal('Accelerator', model.accelerator, 'L');

                if (body.length > 0) {
                    autoTable(doc, {
                        startY: finalY + 5,
                        head: [['Material', 'Amount']],
                        body: body,
                        theme: 'striped',
                        headStyles: { fillColor: [96, 125, 139] },
                        styles: { fontSize: 9 },
                        columnStyles: { 0: { fontStyle: 'bold' } }
                    });
                    finalY = ((doc as any).lastAutoTable.finalY + 10);
                } else {
                    doc.setFontSize(9);
                    doc.text("No material usage recorded.", 14, finalY + 10);
                    finalY += 20;
                }
            };
            if (totalUsedMaterialFromEvents && totalUsedMaterialFromEvents.length > 0) {
                const e = totalUsedMaterialFromEvents[0];
                const hasData = e.cement1 > 0 || e.cement2 > 0 || e.additive1 > 0 || e.accelerator > 0;
                if (hasData) {
                    renderTable("Registered from Events", totalUsedMaterialFromEvents[0]);
                }
            }
        }

        // Add Project Image
        if (projectImage) {
            try {
                const imgProps = doc.getImageProperties(projectImage);
                const pageWidth = doc.internal.pageSize.getWidth();
                const margin = 14;
                const maxImgWidth = pageWidth - (margin * 2);
                const maxImgHeight = 120;

                let imgWidth = imgProps.width;
                let imgHeight = imgProps.height;

                // Scale to fit width
                if (imgWidth > maxImgWidth) {
                    const ratio = maxImgWidth / imgWidth;
                    imgWidth = maxImgWidth;
                    imgHeight = imgHeight * ratio;
                }

                // Scale to fit height
                if (imgHeight > maxImgHeight) {
                    const ratio = maxImgHeight / imgHeight;
                    imgHeight = maxImgHeight;
                    imgWidth = imgWidth * ratio;
                }

                if (finalY + imgHeight > doc.internal.pageSize.getHeight() - 20) {
                    doc.addPage();
                    finalY = 20;
                }

                doc.addImage(projectImage, imgProps.fileType, margin, finalY, imgWidth, imgHeight);
                finalY += imgHeight + 10;
            } catch (error) {
                console.warn("Could not add project image to PDF", error);
            }
        }

        // Add Additional Data
        if (projectAdditionalData) {
            const { wcTestData, additionalInfo } = projectAdditionalData;

            // WC Test Data Table
            if (wcTestData) {
                const hasWcData = Object.values(wcTestData).some((arr: any) => arr && arr.some((val: any) => val && val.trim() !== ''));

                if (hasWcData) {
                    if (finalY > doc.internal.pageSize.getHeight() - 40) {
                        doc.addPage();
                        finalY = 20;
                    }

                    doc.setFontSize(12);
                    doc.text("WC Test Results", 14, finalY);
                    finalY += 5;

                    autoTable(doc, {
                        startY: finalY,
                        head: [['Test Type', 'Test 1', 'Test 2', 'Test 3', 'Test 4']],
                        body: [
                            ['Marsh-viscosity', ...(wcTestData.marshViscosity || ['', '', '', ''])],
                            ['Self-weight control', ...(wcTestData.selfWeightControl || ['', '', '', ''])],
                            ['Water excretion', ...(wcTestData.waterExcretion || ['', '', '', ''])],
                            ['Unbundling', ...(wcTestData.unbundling || ['', '', '', ''])]
                        ],
                        theme: 'grid',
                        styles: { fontSize: 9 },
                        headStyles: { fillColor: [96, 125, 139] }
                    });
                    finalY = (doc as any).lastAutoTable.finalY + 10;
                }
            }

            // Additional Info Table
            if (additionalInfo) {
                const infoRows = [];
                const labels = {
                    rigging: 'Up and Down Rigging',
                    startDrilling: 'Start Drilling',
                    curingTime: 'Curing Time',
                    boreMeter: 'Bore Meter',
                    usedPackers: 'Used Packers',
                    waterLeakage: 'Water Leakage',
                    waterLoss: 'Water Loss Measurements',
                    comments: 'Comments'
                };

                for (const [key, label] of Object.entries(labels)) {
                    if (additionalInfo[key] && additionalInfo[key].trim() !== '') {
                        infoRows.push([label, additionalInfo[key]]);
                    }
                }

                if (infoRows.length > 0) {
                    if (finalY > doc.internal.pageSize.getHeight() - 40) {
                        doc.addPage();
                        finalY = 20;
                    }

                    doc.setFontSize(12);
                    doc.text("Additional Project Information", 14, finalY);
                    finalY += 5;

                    autoTable(doc, {
                        startY: finalY,
                        body: infoRows,
                        theme: 'grid',
                        styles: { fontSize: 9 },
                        columnStyles: {
                            0: { fontStyle: 'bold', cellWidth: 60, fillColor: [245, 245, 245] },
                            1: { cellWidth: 'auto' }
                        }
                    });
                    finalY = (doc as any).lastAutoTable.finalY + 10;
                }
            }
        }

        return finalY;
    }

    private static DrawCementBarChart(
        doc: jsPDF,
        items: { holeNo: number; cement: number }[],
        startX: number, startY: number,
        chartW: number, chartH: number
    ): number {
        if (!items.length) return startY + chartH;

        const AMV_BLUE: [number, number, number]  = [96, 125, 139];
        const AXIS_COL: [number, number, number]  = [75, 94, 109];
        const GRID_COL: [number, number, number]  = [220, 224, 228];
        const BG_COL:   [number, number, number]  = [255, 255, 255];

        const padL = 14, padR = 6, padT = 10, padB = 12;
        const plotX = startX + padL;
        const plotY = startY + padT;
        const plotW = chartW - padL - padR;
        const plotH = chartH - padT - padB;

        const maxVal = Math.max(...items.map(d => d.cement), 1);

        doc.setFillColor(...BG_COL);
        doc.rect(plotX, plotY, plotW, plotH, 'F');

        const gridLines = 4;
        doc.setFontSize(5.5);
        doc.setFont('helvetica', 'normal');
        for (let i = 0; i <= gridLines; i++) {
            const val = (maxVal / gridLines) * i;
            const gy  = plotY + plotH - (val / maxVal) * plotH;
            doc.setDrawColor(...GRID_COL);
            doc.setLineWidth(0.2);
            doc.line(plotX, gy, plotX + plotW, gy);
            doc.setTextColor(...AXIS_COL);
            doc.text(Math.round(val).toLocaleString(), plotX - 1.5, gy + 0.8, { align: 'right' });
        }

        const spacing = plotW / items.length;
        const barW    = Math.max(spacing * 0.85, 0.5);
        doc.setFillColor(...AMV_BLUE);
        items.forEach((item, i) => {
            const barH = (item.cement / maxVal) * plotH;
            if (barH <= 0) return;
            const bx = plotX + i * spacing + (spacing - barW) / 2;
            const by = plotY + plotH - barH;
            doc.rect(bx, by, barW, barH, 'F');
        });

        const minSpacingMm = 5;
        const step = Math.ceil(minSpacingMm / spacing);
        doc.setFontSize(5);
        doc.setTextColor(...AXIS_COL);
        items.forEach((item, i) => {
            if (i % step !== 0) return;
            const cx = plotX + i * spacing + spacing / 2;
            doc.text(`H${item.holeNo}`, cx, plotY + plotH + 4, { align: 'center' });
        });

        doc.setDrawColor(...AXIS_COL);
        doc.setLineWidth(0.4);
        doc.line(plotX, plotY, plotX, plotY + plotH);
        doc.line(plotX, plotY + plotH, plotX + plotW, plotY + plotH);

        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...AXIS_COL);
        doc.text('Total Cement per Hole (kg)', startX + chartW / 2, startY + 6, { align: 'center' });

        doc.setLineWidth(0.2);
        doc.setDrawColor(0, 0, 0);
        doc.setTextColor(0, 0, 0);

        return startY + chartH;
    }

    static AddSummarySection(data: any, doc: jsPDF, finalY: number) {
        const summaryData = data.totalConsumedMaterialOverviewFromPumpsData;
        if (summaryData && summaryData.length > 0) {

            doc.setFontSize(14);
            doc.text("Summary", 14, finalY + 10);
            finalY = this.AddHeaderSubtext(data, doc, finalY + 15);
            finalY += 10;
            const chartItems = summaryData.map((item: any) => ({
                holeNo: item.holeNo,
                cement: (item.cement1 || 0) + (item.cement2 || 0)
            }));
            finalY = this.DrawCementBarChart(doc, chartItems, 14, finalY, 180, 80);
            finalY += 5;

            autoTable(doc, {
                startY: finalY + 15,
                head: [['Hole No', 'Max Pressure', 'Cement 1', 'Cement 2', 'Add 1', 'Add 2', 'Add 3', 'Accel']],
                body: summaryData.map((item: any) => [
                    item.holeNo,
                    (item.maxPressure || 0).toFixed(1),
                    (item.cement1 || 0).toFixed(1),
                    (item.cement2 || 0).toFixed(1),
                    (item.additive1 || 0).toFixed(2),
                    (item.additive2 || 0).toFixed(2),
                    (item.additive3 || 0).toFixed(2),
                    (item.accelerator || 0).toFixed(2)
                ]),
                theme: 'striped',
                headStyles: { fillColor: [96, 125, 139] },
                styles: { fontSize: 9, halign: 'center' },
                columnStyles: {
                    0: { halign: 'left', fontStyle: 'bold' }
                }
            });
            finalY = (doc as any).lastAutoTable.finalY;
        }
        return finalY;
    }

    static AddSetupSection(data: any, doc: jsPDF, finalY: number) {
        const setupData = data.setupData;
        const recipeData = data.recipeSequenceData;

        if ((setupData && setupData.length > 0) || (recipeData && recipeData.length > 0)) {
            doc.setFontSize(14);
            doc.text("Setup", 14, finalY + 10);
            finalY = this.AddHeaderSubtext(data, doc, finalY + 15);
            finalY += 10;

            if (setupData && setupData.length > 0) {
                doc.setFontSize(11);
                doc.text("Pump Setup History", 14, finalY);
                autoTable(doc, {
                    startY: finalY + 5,
                    head: [['Time Stamp', 'Pump ID', 'End Pressure (bar)', 'Stable Time (s)', 'End Amount (L)']],
                    body: setupData.flat().map((s: any) => [
                        s.timestamp ? new Date(s.timestamp).toLocaleString() : '',
                        s.pumpId,
                        Number(s.endPressure ?? 0).toFixed(2),
                        Number(s.stableTime ?? 0).toFixed(2),
                        Number(s.endAmount ?? 0).toFixed(2)
                    ]),
                    theme: 'striped',
                    headStyles: { fillColor: [96, 125, 139] },
                    styles: { fontSize: 9, halign: 'center' },
                    columnStyles: {
                        0: { halign: 'left' }
                    }
                });
                finalY = (doc as any).lastAutoTable.finalY + 10;
            }

            if (recipeData && recipeData.length > 0) {
                if (finalY > 250) {
                    doc.addPage();
                    finalY = 20;
                }
                doc.setFontSize(11);
                doc.text("Recipe & Sequence Information", 14, finalY);
                finalY += 10;

                recipeData.forEach((r: any) => {
                    if (finalY > 240) {
                        doc.addPage();
                        finalY = 20;
                    }

                    doc.setFontSize(10);
                    doc.setFont(doc.getFont().fontName, 'bold');
                    const time = r.timestamp ? new Date(r.timestamp).toLocaleString() : '';
                    doc.text(`ID: ${r.recipeID} - ${r.recipeName}`, 14, finalY);

                    doc.setFontSize(9);
                    doc.setFont(doc.getFont().fontName, 'normal');
                    doc.text(`VC: ${Number(r.vcValue || 0).toFixed(2)} | Vol: ${Number(r.volume || 0).toFixed(2)} L | Cem: ${Number(r.cement || 0).toFixed(2)} kg | Time: ${time}`, 14, finalY + 5);

                    const visibleSteps = (r.sequence || []).filter((step: any) =>
                        step.materialName !== 'None' ||
                        step.volume !== 0 ||
                        step.mixingTime !== 0
                    );

                    if (visibleSteps.length > 0) {
                        const seqBody = visibleSteps.map((s: any) => [
                            s.sequenceNo,
                            s.materialName,
                            Number(s.volume || 0).toFixed(2),
                            Number(s.mixingTime || 0).toFixed(2)
                        ]);

                        autoTable(doc, {
                            startY: finalY + 7,
                            head: [['Step', 'Material', 'Volume (L)', 'Mixing Time (s)']],
                            body: seqBody,
                            theme: 'striped',
                            headStyles: { fillColor: [96, 125, 139] },
                            styles: { fontSize: 8, halign: 'center' },
                            columnStyles: {
                                1: { halign: 'left' }
                            },
                            margin: { left: 14 }
                        });
                        finalY = (doc as any).lastAutoTable.finalY + 10;
                    } else {
                        finalY += 15;
                    }
                });
            }
        }
        return finalY;
    }

    static AddStatusSection(data: any, doc: jsPDF, finalY: number) {
        const statusData = data.statusDataList || data.statusDataList;
        if (statusData && statusData.length > 0) {
            doc.setFontSize(14);
            doc.text("Hole Status Overview", 14, finalY + 10);
            finalY =this.AddHeaderSubtext(data, doc, finalY + 15);
            finalY += 10;

            // --- Calculations ---
            const total = statusData.length;
            const finishedCount = statusData.filter((i: any) => (i.eventType || '').toLowerCase().includes('reached')).length;
            const pendingCount = statusData.filter((i: any) => (i.eventType || '').toLowerCase().includes('not finished')).length;

            const finishedPct = total > 0 ? (finishedCount / total) : 0;
            const pendingPct = total > 0 ? (pendingCount / total) : 0;

            let totalSeconds = 0;
            statusData.forEach((item: any) => {
                if (item.totalTimeMs) {
                    totalSeconds += item.totalTimeMs / 1000;
                }
            });
            const avgSeconds = total > 0 ? totalSeconds / total : 0;

            const formatDurationSeconds = (seconds: number) => {
                const h = Math.floor(seconds / 3600);
                const m = Math.floor((seconds % 3600) / 60);
                const s = Math.floor(seconds % 60);
                const pad = (n: number) => n.toString().padStart(2, '0');
                return `${pad(h)}:${pad(m)}:${pad(s)}`;
            };

            const totalDurationStr = formatDurationSeconds(totalSeconds);
            const avgDurationStr = formatDurationSeconds(avgSeconds);

            // --- Drawing Cards ---
            const cardWidth = 88;
            const cardHeight = 35;
            const gap = 6;
            const startY = finalY;

            // Helper to draw card background and header
            const drawCardBase = (x: number, title: string) => {
                doc.setDrawColor(226, 232, 240); // border
                doc.setFillColor(255, 255, 255); // white bg
                doc.roundedRect(x, startY, cardWidth, cardHeight, 1, 1, 'FD');

                // Header
                doc.setFillColor(248, 250, 252); // light grey header
                doc.roundedRect(x, startY, cardWidth, 8, 1, 1, 'F');
                doc.rect(x, startY + 5, cardWidth, 3, 'F'); // Square off bottom corners of header

                doc.setFontSize(10);
                doc.setFont(doc.getFont().fontName, 'bold');
                doc.setTextColor(60, 60, 60);
                doc.text(title, x + 4, startY + 5.5);
            };

            // 1. Hole Status Card
            drawCardBase(14, "Hole Status");
            const barX = 18;
            const barY = startY + 15;
            const barWidth = cardWidth - 8;
            const barHeight = 6;

            // Progress Bar Background
            doc.setFillColor(238, 238, 238);
            doc.roundedRect(barX, barY, barWidth, barHeight, 2, 2, 'F');

            // Finished Segment (Green)
            if (finishedPct > 0) {
                doc.setFillColor(76, 175, 80);
                doc.rect(barX, barY, barWidth * finishedPct, barHeight, 'F');
            }
            // Pending Segment (Orange)
            if (pendingPct > 0) {
                doc.setFillColor(251, 140, 0);
                doc.rect(barX + (barWidth * finishedPct), barY, barWidth * pendingPct, barHeight, 'F');
            }

            // Legend
            const legendY = barY + 12;
            doc.setFontSize(8);

            const drawLegendItem = (x: number, color: [number, number, number], label: string, value: number) => {
                doc.setFillColor(...color);
                doc.circle(x, legendY - 1, 1.5, 'F');
                doc.setTextColor(100);
                doc.setFont(doc.getFont().fontName, 'normal');
                doc.text(label, x + 4, legendY);
                doc.setTextColor(0);
                doc.setFont(doc.getFont().fontName, 'bold');
                doc.text(value.toString(), x + 4 + doc.getTextWidth(label) + 2, legendY);
            };

            drawLegendItem(barX + 2, [76, 175, 80], "Finished:", finishedCount);
            drawLegendItem(barX + 40, [251, 140, 0], "Pending:", pendingCount);

            // 2. Production Summary Card
            const card2X = 14 + cardWidth + gap;
            drawCardBase(card2X, "Production Summary");

            const contentY = startY + 20;

            // Total Duration
            doc.setFontSize(14);
            doc.setTextColor(55, 71, 79);
            doc.text(totalDurationStr, card2X + 22, contentY, { align: 'center' });
            doc.setFontSize(7);
            doc.setTextColor(150);
            doc.setFont(doc.getFont().fontName, 'bold');
            doc.text("TOTAL DURATION", card2X + 22, contentY + 5, { align: 'center' });

            // Divider
            doc.setDrawColor(220, 220, 220);
            doc.line(card2X + 44, startY + 12, card2X + 44, startY + 30);

            // Avg Duration
            doc.setFontSize(14);
            doc.setTextColor(55, 71, 79);
            doc.text(avgDurationStr, card2X + 66, contentY, { align: 'center' });
            doc.setFontSize(7);
            doc.setTextColor(150);
            doc.text("AVG DURATION", card2X + 66, contentY + 5, { align: 'center' });

            finalY += cardHeight + 10;

            autoTable(doc, {
                startY: finalY,
                head: [['Hole', 'Status', 'Start', 'Stop', 'Duration', 'Volume']],
                body: statusData.map((s: any) => [
                    s.holeNo,
                    s.eventType,
                    s.startTime ? new Date(s.startTime).toLocaleTimeString() : '-',
                    s.stopTime ? new Date(s.stopTime).toLocaleTimeString() : '-',
                    s.totalTime,
                    s.volume ? s.volume.toFixed(1) : '0'
                ]),
                theme: 'striped',
                headStyles: { fillColor: [96, 125, 139] }
            });
            finalY = (doc as any).lastAutoTable.finalY;
        }
        return finalY;
    }

    static AddMaterialSection(data: any, doc: jsPDF, finalY: number) {
        const totalUsedMaterialFromEvents = data.totalUsedMaterialFromEvents;
        const totalConsumedMaterialOverviewFromPumpsData = data.totalConsumedMaterialOverviewFromPumpsData;
        if ((totalUsedMaterialFromEvents && totalUsedMaterialFromEvents.length > 0) || (totalConsumedMaterialOverviewFromPumpsData && totalConsumedMaterialOverviewFromPumpsData.length > 0)) {
            const extractTotalMaterialConsumedFromPumps = (() => {
            const total = {
                cement1: 0,
                cement2: 0,
                additive1: 0,
                additive2: 0,
                accelerator: 0,
            };
            for (const item of (totalConsumedMaterialOverviewFromPumpsData || []))
            {
                total.cement1 += item.cement1 || 0;
                total.cement2 += item.cement2 || 0;
                total.additive1 += item.additive1 || 0;
                total.additive2 += item.additive2 || 0;
                total.accelerator += item.accelerator || 0;
            }
            return total;
        })();

            doc.setFontSize(14);
            doc.text("Material Consumption Totals", 14, finalY + 10);
            finalY = this.AddHeaderSubtext(data, doc, finalY + 15);
            finalY += 10;

            const renderTable = (title: string, model: any) => {
                if (!model) return;

                if (finalY > 240) {
                    doc.addPage();
                    finalY = 20;
                }

                doc.setFontSize(11);
                doc.text(title, 14, finalY);

                const body : any[] = [];
                const pushIfVal = (label: string, val: number, unit: string) => {
                    if (val > 0) body.push([label, val.toFixed(2) + ' ' + unit]);
                };

                pushIfVal('Cement 1', model.cement1, 'kg');
                pushIfVal('Cement 2', model.cement2, 'kg');
                pushIfVal('Additive 1', model.additive1, 'L');
                pushIfVal('Additive 2', model.additive2, 'L');
                pushIfVal('Additive 3', model.additive3, 'L');
                pushIfVal('Additive 4', model.additive4, 'L');
                pushIfVal('Additive 5', model.additive5, 'L');
                pushIfVal('Additive 6', model.additive6, 'L');
                pushIfVal('Accelerator', model.accelerator, 'L');

                if (body.length > 0) {
                    autoTable(doc, {
                        startY: finalY + 5,
                        head: [['Material', 'Amount']],
                        body: body,
                        theme: 'striped',
                        headStyles: { fillColor: [96, 125, 139] },
                        styles: { fontSize: 9 },
                        columnStyles: { 0: { fontStyle: 'bold' } }
                    });
                    finalY = (doc as any).lastAutoTable.finalY + 10;
                } else {
                    doc.setFontSize(9);
                    doc.text("No material usage recorded.", 14, finalY + 10);
                    finalY += 20;
                }
            };

            if (totalUsedMaterialFromEvents) {
                const e = totalUsedMaterialFromEvents;
                const hasData = e.cement1 > 0 || e.cement2 > 0 || e.additive1 > 0 || e.accelerator > 0;
                if (hasData) {
                    renderTable("Registered from Events", totalUsedMaterialFromEvents);
                }
            }

            if (extractTotalMaterialConsumedFromPumps) {
                renderTable("Calculated from Pumps", extractTotalMaterialConsumedFromPumps);
            }

            if (totalConsumedMaterialOverviewFromPumpsData && totalConsumedMaterialOverviewFromPumpsData.length > 0) {
                if (finalY > 240) {
                    doc.addPage();
                    finalY = 20;
                }
                const chartItems = totalConsumedMaterialOverviewFromPumpsData.map((item: any) => ({
                    holeNo: item.holeNo,
                    cement: (item.cement1 || 0) + (item.cement2 || 0)
                }));
                finalY = this.DrawCementBarChart(doc, chartItems, 14, finalY, 180, 80);
                finalY += 5;

                doc.setFontSize(11);
                doc.text("Consumption per Hole", 14, finalY);

                autoTable(doc, {
                    startY: finalY + 5,
                    head: [['Hole', 'Max Press', 'Cement 1', 'Cement 2', 'Add 1', 'Add 2', 'Add 3', 'Accel']],
                    body: totalConsumedMaterialOverviewFromPumpsData.map((m: any) => [
                        m.holeNo,
                        (m.maxPressure || 0).toFixed(1),
                        (m.cement1 || 0).toFixed(1),
                        (m.cement2 || 0).toFixed(1),
                        (m.additive1 || 0).toFixed(2),
                        (m.additive2 || 0).toFixed(2),
                        (m.additive3 || 0).toFixed(2),
                        (m.accelerator || 0).toFixed(2)
                    ]),
                    theme: 'striped',
                    headStyles: { fillColor: [96, 125, 139] },
                    styles: { fontSize: 9, halign: 'center' },
                    columnStyles: {
                        0: { halign: 'left', fontStyle: 'bold' }
                    }
                });
                finalY = (doc as any).lastAutoTable.finalY;
            }
        }
        return finalY;
    }

    static AddPumpInformationSection(data: any, doc: jsPDF, finalY: number) {
        const pumpInfo = data.pumpInformationDataList || data.pumpInfo;
        if (pumpInfo && pumpInfo.length > 0) {
            doc.setFontSize(14);
            doc.text("Pump Information", 14, finalY + 10);
            finalY = this.AddHeaderSubtext(data, doc, finalY + 15);
            finalY += 5;

            // Calculate pump statistics
            const netRow = pumpInfo.find((r: any) => r.dataHeader === 'Net Time');
            const idleRow = pumpInfo.find((r: any) => r.dataHeader === 'Idle Time');

            const parse = (val: string | undefined) => {
                if (!val) return 0;
                const f = parseFloat(val);
                return isNaN(f) ? 0 : f;
            };

            const getStats = (key: string) => {
                const net = netRow ? parse((netRow as any)[key]) : 0;
                const idle = idleRow ? parse((idleRow as any)[key]) : 0;
                const total = net + idle;
                return {
                    net,
                    idle,
                    netPct: total > 0 ? (net / total) * 100 : 0,
                    idlePct: total > 0 ? (idle / total) * 100 : 0
                };
            };

            const pumpStatsData = [
                { label: 'Pump 1', ...getStats('pumpOne') },
                { label: 'Pump 2', ...getStats('pumpTwo') },
                { label: 'Pump 3', ...getStats('pumpThree') },
                { label: 'Pump 4', ...getStats('pumpFour') },
                { label: 'System Total', ...getStats('totalPumpTime') },
            ];

            // Draw pump stat cards
            const cardWidth = 34;
            const cardHeight = 25;
            const cardGap = 3;
            let currentX = 14;
            const startY = finalY;

            pumpStatsData.forEach(pump => {
                doc.setFillColor(248, 250, 252); // light grey bg
                doc.setDrawColor(226, 232, 240); // border
                doc.rect(currentX, startY, cardWidth, cardHeight, 'FD');

                doc.setFontSize(8);
                doc.setFont('bold');
                doc.setTextColor(71, 85, 105);
                doc.text(pump.label, currentX + 2, startY + 5);

                const barY = startY + 8;
                const barWidth = cardWidth - 4;
                const barHeight = 4;

                doc.setFillColor(226, 232, 240); // bar bg
                doc.rect(currentX + 2, barY, barWidth, barHeight, 'F');

                const netWidth = (barWidth * pump.netPct) / 100;
                doc.setFillColor(34, 197, 94); // green
                doc.rect(currentX + 2, barY, netWidth, barHeight, 'F');

                const idleWidth = (barWidth * pump.idlePct) / 100;
                doc.setFillColor(245, 158, 11); // amber
                doc.rect(currentX + 2 + netWidth, barY, idleWidth, barHeight, 'F');

                doc.setFontSize(7);
                doc.setTextColor(100, 116, 139);
                doc.text(`Net: ${this._formatDurationFromDays(pump.net)}`, currentX + 2, barY + barHeight + 5);
                doc.text(`Idle: ${this._formatDurationFromDays(pump.idle)}`, currentX + 2, barY + barHeight + 10);

                currentX += cardWidth + cardGap;
            });

            finalY += cardHeight + 10;
            const formatValue = (header: string, value: any) => {
                if (value === "0" || value === "" || !value) return "—";
                const isDuration = ["Total Time", "Net Time", "Idle Time"].includes(header);
                if (isDuration) {
                    const totalDays = parseFloat(value);
                    if (isNaN(totalDays)) return value;
                    return this._formatDurationFromDays(totalDays);
                }
                return value;
            };

            autoTable(doc, {
                startY: finalY,
                head: [['Description', 'Pump 1', 'Pump 2', 'Pump 3', 'Pump 4', 'Total']],
                body: pumpInfo.map((p: any) => [
                    p.dataHeader,
                    formatValue(p.dataHeader, p.pumpOne),
                    formatValue(p.dataHeader, p.pumpTwo),
                    formatValue(p.dataHeader, p.pumpThree),
                    formatValue(p.dataHeader, p.pumpFour),
                    formatValue(p.dataHeader, p.totalPumpTime)
                ]),
                theme: 'grid',
                headStyles: { fillColor: [96, 125, 139] },
                columnStyles: {
                    0: { fontStyle: 'bold' }
                }
            });
            finalY = (doc as any).lastAutoTable.finalY;

            const concurrent = data.ConcurrentPumpOverview;
            if (concurrent) {
                const total = concurrent.onePumpMs + concurrent.twoPumpMs + concurrent.threePumpMs + concurrent.fourPumpMs;
                const pct = (ms: number) => total > 0 ? ((ms / total) * 100).toFixed(1) + '%' : '—';
                const msToHHmmss = (ms: number) => {
                    const s = Math.floor(ms / 1000);
                    const pad = (n: number) => n.toString().padStart(2, '0');
                    return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
                };
                const twoPlusMs = concurrent.twoPumpMs + concurrent.threePumpMs + concurrent.fourPumpMs;
                const threePlusMs = concurrent.threePumpMs + concurrent.fourPumpMs;

                autoTable(doc, {
                    startY: finalY + 10,
                    head: [['Concurrent Pump Operation', 'Time', '% of Total']],
                    body: [
                        ['1 pump', concurrent.onePumpTime, pct(concurrent.onePumpMs)],
                        ['2 pumps simultaneously', concurrent.twoPumpTime, pct(concurrent.twoPumpMs)],
                        ['3 pumps simultaneously', concurrent.threePumpTime, pct(concurrent.threePumpMs)],
                        ['4 pumps simultaneously', concurrent.fourPumpTime, pct(concurrent.fourPumpMs)],
                        ['2+ pumps (total)', msToHHmmss(twoPlusMs), pct(twoPlusMs)],
                        ['3+ pumps (total)', msToHHmmss(threePlusMs), pct(threePlusMs)],
                    ],
                    theme: 'grid',
                    headStyles: { fillColor: [96, 125, 139] },
                    columnStyles: { 0: { fontStyle: 'bold' } },
                    didParseCell: (hookData: any) => {
                        if (hookData.section === 'body' && hookData.row.index >= 4) {
                            hookData.cell.styles.fontStyle = 'bold';
                        }
                    }
                });
                finalY = (doc as any).lastAutoTable.finalY;
            }
        }
        return finalY;
    }

    static AddPumpRecipeGanttSection(data: any, doc: jsPDF, finalY: number): number {
        const ganttData = data.pumprecipeGanttData;
        if (ganttData && ganttData.headers && ganttData.rows && ganttData.rows.length > 0) {
            finalY = 5;

            const pageWidth = doc.internal.pageSize.getWidth();
            const marginTotal = 10;
            const availableWidth = pageWidth - marginTotal;
            const firstColWidth = 6;
            const columnsPerPage = 24;
            const dynamicCellWidth = (availableWidth - firstColWidth) / columnsPerPage;

            doc.setFontSize(14);
            doc.text("Pump & Recipe Gantt Chart", 14, finalY + 10);
            finalY = this.AddHeaderSubtext(data, doc, finalY + 15);

            const headers = ['H', ...ganttData.headers.map((h: any) => {
                const d = new Date(h);
                return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
            })];

            const body = ganttData.rows.map((row: any) => {
                const rowData = [row.holeNo];
                row.cells.forEach((cell: any) => {
                    const rgb = cell ? this._pumpIdToRgb(cell.pumpId) : [255, 255, 255];
                    rowData.push({
                        content: cell ? `${cell.pumpId}\nR${cell.recipeId}` : '',
                        styles: {
                            fillColor: cell ? rgb : null,
                            textColor: cell ? [255, 255, 255] : [0, 0, 0],
                            halign: 'center',
                            fontStyle: 'bold',
                            cellWidth: dynamicCellWidth
                        }
                    });
                });
                return rowData;
            });

            autoTable(doc, {
                startY: finalY,
                head: [headers],
                body: body,
                theme: 'grid',
                styles: { fontSize: 5, cellPadding: 0.5 },
                headStyles: { fillColor: [96, 125, 139], fontSize: 6 },
                columnStyles: {
                    0: { fontStyle: 'bold', cellWidth: firstColWidth, fillColor: [240, 240, 240] }
                },
                horizontalPageBreak: true,
                horizontalPageBreakRepeat: 0,
                margin: { left: 5, right: 5, top: 10, bottom: 10 },
                tableWidth: 'auto'
            });

            finalY = (doc as any).lastAutoTable.finalY;

            const uniquePumps = new Map<number, string>();
            const uniqueRecipes = new Map<number, string>();
            ganttData.rows.forEach((row: any) => {
                row.cells.forEach((cell: any) => {
                    if (cell) {
                        if (!uniquePumps.has(cell.pumpId)) uniquePumps.set(cell.pumpId, cell.pumpName);
                        if (!uniqueRecipes.has(cell.recipeId)) uniqueRecipes.set(cell.recipeId, cell.recipeName);
                    }
                });
            });

            const pageHeight = doc.internal.pageSize.getHeight();

            if (uniquePumps.size > 0) {
                finalY += 8;
                if (finalY > pageHeight - 20) { doc.addPage(); finalY = 20; }
                doc.setFontSize(10);
                doc.setTextColor(0);
                doc.text("Pump Reference:", 14, finalY);
                finalY += 5;
                uniquePumps.forEach((name, id) => {
                    if (finalY > pageHeight - 10) { doc.addPage(); finalY = 20; }
                    const rgb = this._pumpIdToRgb(id);
                    doc.setFillColor(rgb[0]!, rgb[1]!, rgb[2]!);
                    doc.rect(14, finalY - 3.5, 4, 4, 'F');
                    doc.setTextColor(0);
                    doc.text(`${id} - ${name}`, 20, finalY);
                    finalY += 6;
                });
            }

            if (uniqueRecipes.size > 0) {
                finalY += 4;
                if (finalY > pageHeight - 20) { doc.addPage(); finalY = 20; }
                doc.setFontSize(10);
                doc.setTextColor(0);
                doc.text("Recipe Reference:", 14, finalY);
                finalY += 5;
                uniqueRecipes.forEach((name, id) => {
                    if (finalY > pageHeight - 10) { doc.addPage(); finalY = 20; }
                    doc.setTextColor(0);
                    doc.text(`R${id} - ${name}`, 14, finalY);
                    finalY += 6;
                });
            }
        }
        return finalY;
    }

    static AddRecipeInformationSection(reportData: any, doc: jsPDF, finalY: number) {
        const { recipeOverviewData } = reportData;
        if (!recipeOverviewData || recipeOverviewData.length === 0) return;
        const recipeStats = (() => {
            let data = recipeOverviewData;

            const grouped = new Map();
            for (const item of data) {
                const key = item.recipeId;
                if (!grouped.has(key)) {
                    grouped.set(key, {
                        recipeId: key,
                        recipeName: item.recipeName,
                        cement: 0,
                        accelerator: 0,
                        totalTimeMs: 0
                    });
                }
                const group = grouped.get(key);
                group.cement += (item.cement || 0);
                group.accelerator += (item.accelerator || 0);

                if (item.totalTimeMs) {
                    group.totalTimeMs += item.totalTimeMs;
                }
            }

            return Array.from(grouped.values()).map(item => {
                return {
                recipeName: item.recipeName,
                cement: item.cement,
                accelerator: item.accelerator,
                totalTimeMs: item.totalTimeMs,
                totalTime: this._calculateTotalTimeFromMs(item.totalTimeMs)
                }
            }).sort((a, b) => b.totalTimeMs - a.totalTimeMs)
        })();

        // Section Title
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.text("Recipe Information", 14, finalY);
        finalY =this.AddHeaderSubtext(reportData, doc, finalY + 15);
        finalY += 10;

        const formatNumber = (num: number) => {
            return (num || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        };

        const totalUse = recipeStats;
        let totalCement = 0;
        let totalAccelerator = 0;
        let totalMs = 0;
        totalUse.forEach((item: any) => {
            totalCement += (item.cement || 0);
            totalAccelerator += (item.accelerator || 0);
            totalMs += item.totalTimeMs;
        });
        const recipeBody = recipeStats.map((r: any) => [
            r.recipeName,
            `${formatNumber(r.cement)} kg`,
            `${formatNumber(r.accelerator)} kg`,
            this._calculateTotalTimeFromMs(r.totalTimeMs)
        ]);


        // 1. Total Usage Table
        doc.setFontSize(12);
        doc.text("Total Usage", 14, finalY);
        finalY += 6;

        autoTable(doc, {
            startY: finalY,
            head: [['Metric', 'Value']],
            body: [
                ['Total Runs', recipeOverviewData.length],
                ['Total Cement', `${formatNumber(totalCement)} kg`],
                ['Total Accelerator', `${formatNumber(totalAccelerator)} kg`],
                ['Total Duration', this._calculateTotalTimeFromMs(totalMs)]
            ],
            theme: 'striped',
            headStyles: { fillColor: [96, 125, 139] }, // AMV Blue-ish
            margin: { left: 14 },
            tableWidth: 100
        });

        finalY = (doc as any).lastAutoTable.finalY + 15;

        // 2. Consumption per Recipe Table
        doc.text("Consumption per Recipe", 14, finalY);
        finalY += 6;

        autoTable(doc, {
            startY: finalY,
            head: [['Recipe Name', 'Cement', 'Accelerator', 'Total Time']],
            body: recipeBody,
            theme: 'striped',
            headStyles: { fillColor: [96, 125, 139] },
            margin: { left: 14 }
        });

        finalY = (doc as any).lastAutoTable.finalY + 15;
        doc.text("Recipe overview", 14, finalY);
        finalY += 6;
        autoTable(doc, {
            startY: finalY,
            head: [['Hole', 'Recipe', 'Start', 'Stop', 'Duration', 'Cement', 'Accelerator']],
            body: recipeOverviewData.map((r: any) => [
                r.holeNo,
                r.recipeName,
                r.startTime ? new Date(r.startTime).toLocaleTimeString() : '',
                r.stopTime ? new Date(r.stopTime).toLocaleTimeString() : '',
                r.totalTime,
                (r.cement || 0).toFixed(1),
                (r.accelerator || 0).toFixed(2)
            ]),
            theme: 'striped',
            headStyles: { fillColor: [96, 125, 139] },
            styles: { fontSize: 9, halign: 'center' },
            columnStyles: {
                0: { halign: 'left' },
                1: { halign: 'left' }
            }
        });
        return (doc as any).lastAutoTable.finalY + 10;
    }

    static AddGeoAnalysisSection(data: any, doc: any, finalY: number): number {
        const raw: any[] = data.totalConsumedMaterialOverviewFromPumpsData || [];
        if (!raw.length) return finalY;

        const PRESSURE_LOW = 10;
        const resolveThreshold = (vals: number[]): number => {
            const freq = new Map<number, number>();
            vals.forEach(v => freq.set(v, (freq.get(v) || 0) + 1));
            const maxFreq = Math.max(...freq.values());
            const top = [...freq.entries()].filter(([, f]) => f === maxFreq).map(([v]) => v);
            if (top.length === 1) return top[0];
            const s = [...vals].sort((a, b) => a - b);
            const m = Math.floor(s.length / 2);
            return s.length % 2 === 0 ? Math.round((s[m - 1] + s[m]) / 2) : s[m];
        };
        const setupSessions: any[][] = data.setupData || [];
        let PRESSURE_HIGH = 25;
        let maxVolumeLimit = 0; // endAmount in litres — different unit to cement kg
        if (setupSessions.length) {
            const latestSession = [...setupSessions].sort((a: any[], b: any[]) => {
                const tA = a.length ? new Date(a[0].timestamp).getTime() : 0;
                const tB = b.length ? new Date(b[0].timestamp).getTime() : 0;
                return tB - tA;
            })[0];
            const pressVals = (latestSession || []).map((c: any) => c.endPressure).filter((v: number) => v > 0);
            if (pressVals.length) PRESSURE_HIGH = resolveThreshold(pressVals);
            const volVals = (latestSession || []).map((c: any) => c.endAmount).filter((v: number) => v > 0);
            if (volVals.length) maxVolumeLimit = resolveThreshold(volVals);
        }
        const getZone = (p: number) => p >= PRESSURE_HIGH ? 'High' : p >= PRESSURE_LOW ? 'Moderate' : 'Low';

        const scatterData = raw
            .map((item: any) => ({
                holeNo:   item.holeNo,
                cement:   (item.cement1 || 0) + (item.cement2 || 0),
                pressure: item.maxPressure || 0,
            }))
            .filter((d: any) => d.cement > 0 || d.pressure > 0)
            .sort((a: any, b: any) => b.pressure - a.pressure);

        if (!scatterData.length) return finalY;

        const statusList: any[] = data.statusDataList || [];
        const stopReasonMap = new Map<number, string>();
        statusList.forEach((s: any) => stopReasonMap.set(s.holeNo, s.eventType || ''));

        const pressures = scatterData.map((d: any) => d.pressure);
        const cements   = scatterData.map((d: any) => d.cement);
        const sum       = (arr: number[]) => arr.reduce((a, b) => a + b, 0);

        doc.setFontSize(14);
        doc.text('Geo Analysis', 14, finalY + 10);
        finalY = this.AddHeaderSubtext(data, doc, finalY + 15);
        finalY += 8;

        // Threshold note
        doc.setFontSize(8);
        doc.setTextColor(75, 94, 109);
        doc.text(`Zones: Low <${PRESSURE_LOW} bar  |  Moderate ${PRESSURE_LOW}-${PRESSURE_HIGH} bar  |  High >=${PRESSURE_HIGH} bar${maxVolumeLimit > 0 ? `  |  Max volume limit: ${maxVolumeLimit} L per pump` : ''}`, 14, finalY);
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(14);
        finalY += 6;

        // KPI row
        autoTable(doc, {
            startY: finalY,
            head: [['Holes Analysed', 'Peak Pressure (bar)', 'Avg Pressure (bar)', 'Total Cement (kg)', `High Zone (≥${PRESSURE_HIGH} bar)`, `Low Zone (<${PRESSURE_LOW} bar)`]],
            body: [[
                scatterData.length,
                Math.max(...pressures).toFixed(1),
                (sum(pressures) / pressures.length).toFixed(1),
                sum(cements).toFixed(0),
                scatterData.filter((d: any) => d.pressure >= PRESSURE_HIGH).length,
                scatterData.filter((d: any) => d.pressure < PRESSURE_LOW).length,
            ]],
            theme: 'grid',
            headStyles: { fillColor: [96, 125, 139], fontSize: 8, halign: 'center' },
            bodyStyles: { halign: 'center', fontStyle: 'bold', fontSize: 10 },
        });
        finalY = doc.lastAutoTable.finalY + 8;

        // Scatter chart
        finalY = this.DrawScatterChart(doc, scatterData, 14, finalY, 183, 85);
        finalY += 6;

        if (finalY > doc.internal.pageSize.getHeight() - 60) {
            doc.addPage();
            finalY = 20;
        }

        // Material by Zone
        const totalCement = sum(cements);
        const zoneBreakdown = (['High', 'Moderate', 'Low'] as const).map(zone => {
            const zoneHoles = scatterData.filter((d: any) => getZone(d.pressure) === zone);
            const zoneCement = sum(zoneHoles.map((d: any) => d.cement));
            return [
                zone,
                zoneHoles.length,
                +zoneCement.toFixed(0),
                totalCement > 0 ? (zoneCement / totalCement * 100).toFixed(1) + '%' : '0%',
                zoneHoles.length > 0 ? (zoneCement / zoneHoles.length).toFixed(1) : '0',
            ];
        });
        const ZONE_RGB: Record<string, [number, number, number]> = {
            High:     [76, 175, 80],
            Moderate: [240, 179, 35],
            Low:      [229, 57, 53],
        };
        autoTable(doc, {
            startY: finalY,
            head: [['Zone', 'Holes', 'Total Cement (kg)', '% of Total', 'Avg kg / Hole']],
            body: zoneBreakdown,
            theme: 'striped',
            headStyles: { fillColor: [96, 125, 139] },
            styles: { fontSize: 9, halign: 'center' },
            columnStyles: { 0: { fontStyle: 'bold' } },
            didParseCell(hookData: any) {
                if (hookData.section === 'body' && hookData.column.index === 0) {
                    const zone = hookData.cell.raw as string;
                    if (ZONE_RGB[zone]) hookData.cell.styles.textColor = ZONE_RGB[zone];
                }
            }
        });
        finalY = doc.lastAutoTable.finalY + 8;

        if (finalY > doc.internal.pageSize.getHeight() - 60) {
            doc.addPage();
            finalY = 20;
        }

        // Per-hole table
        autoTable(doc, {
            startY: finalY,
            head: [['Hole No', 'Cement (kg)', 'Max Pressure (bar)', 'Zone', 'Stop Reason']],
            body: scatterData.map((d: any) => [
                d.holeNo,
                d.cement.toFixed(1),
                d.pressure.toFixed(1),
                getZone(d.pressure),
                stopReasonMap.get(d.holeNo) || '',
            ]),
            theme: 'striped',
            headStyles: { fillColor: [96, 125, 139] },
            styles: { fontSize: 9, halign: 'center' },
            columnStyles: {
                0: { halign: 'left', fontStyle: 'bold' },
                3: { fontStyle: 'bold' },
                4: { halign: 'left' },
            },
            didParseCell(hookData: any) {
                if (hookData.section === 'body' && hookData.column.index === 3) {
                    const zone = hookData.cell.raw as string;
                    if (zone === 'High')     { hookData.cell.styles.textColor = [76, 175, 80]; }
                    else if (zone === 'Low') { hookData.cell.styles.textColor = [229, 57, 53]; }
                    else                     { hookData.cell.styles.textColor = [240, 179, 35]; }
                }
                if (hookData.section === 'body' && hookData.column.index === 4) {
                    const sr = (hookData.cell.raw as string || '').toLowerCase();
                    if (sr.includes('reached'))                                    hookData.cell.styles.textColor = [76, 175, 80];
                    else if (sr.includes('not finalized') || sr.includes('working')) hookData.cell.styles.textColor = [240, 179, 35];
                    else if (sr.includes('error') || sr.includes('stop'))           hookData.cell.styles.textColor = [229, 57, 53];
                }
            }
        });
        finalY = doc.lastAutoTable.finalY;
        return finalY;
    }

    static AddHoleGraphsSection(data: any, doc: jsPDF, finalY: number): number {
        const holeGraphData = data.holeGraphData;
        if (!holeGraphData || holeGraphData.length === 0) return finalY;

        doc.setFontSize(14);
        doc.text("Hole Graphs", 14, finalY + 10);
        finalY = this.AddHeaderSubtext(data, doc, finalY + 15);
        finalY += 10;

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 14;
        const graphHeight = 60;
        const graphWidth = pageWidth - (margin * 2);

        holeGraphData.forEach((hole: any) => {
            if (finalY + graphHeight + 20 > pageHeight) {
                doc.addPage();
                finalY = 20;
            }

            doc.setFontSize(10);
            doc.setTextColor(0);
            doc.text(`Hole ${hole.holeNo}`, margin, finalY);

            const startY = finalY + 5;
            const endY = startY + graphHeight;
            const startX = margin;
            const endX = margin + graphWidth;

            // Background
            doc.setDrawColor(220);
            doc.setFillColor(250, 250, 250);
            doc.rect(startX, startY, graphWidth, graphHeight, 'FD');

            if (!hole.data || hole.data.length === 0) {
                doc.text("No data", startX + graphWidth / 2, startY + graphHeight / 2, { align: 'center' });
                finalY += graphHeight + 15;
                return;
            }

            const dataPoints = hole.data;
            const times = dataPoints.map((d: any) => new Date(d.timestamp).getTime());
            const minTime = Math.min(...times);
            const maxTime = Math.max(...times);
            const timeRange = maxTime - minTime || 1;

            const maxFlow = Math.max(...dataPoints.map((d: any) => d.flow || 0));
            const maxPressure = Math.max(...dataPoints.map((d: any) => d.pressure || 0));
            const maxLeft = Math.max(maxFlow, maxPressure, 10) * 1.1;

            const maxVol = Math.max(...dataPoints.map((d: any) => d.volume || 0));
            const maxCem = Math.max(...dataPoints.map((d: any) => d.cement || 0));
            let maxRightRaw = Math.max(maxVol, maxCem, 10);
            const stepRight = 500;
            const maxRight = Math.ceil(maxRightRaw / stepRight) * stepRight;

            const getX = (t: number) => startX + ((t - minTime) / timeRange) * graphWidth;
            const getYLeft = (v: number) => endY - (v / maxLeft) * graphHeight;
            const getYRight = (v: number) => endY - (v / maxRight) * graphHeight;

            // Draw Grid and Labels
            doc.setFontSize(7);
            doc.setTextColor(100);
            doc.setDrawColor(240);

            // Left Axis (Flow/Press) - 10 steps
            const stepLeft = maxLeft / 10;
            for (let i = 0; i <= 10; i++) {
                const v = i * stepLeft;
                const y = getYLeft(v);
                doc.line(startX, y, endX, y);
                doc.text(v.toFixed(0), startX - 1, y + 1, { align: 'right' });
            }

            // Right Axis (Vol/Cem) - 500 increments
            for (let v = 0; v <= maxRight; v += stepRight) {
                const y = getYRight(v);
                if (y < startY) continue;
                doc.text(v.toFixed(0), endX + 1, y + 1, { align: 'left' });
            }

            // X-Axis Timestamps
            const startDate = new Date(minTime);
            const endDate = new Date(maxTime);
            const midDate = new Date(minTime + (maxTime - minTime) / 2);
            const fmtTime = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

            doc.text(fmtTime(startDate), startX, endY + 4, { align: 'left' });
            doc.text(fmtTime(midDate), startX + (graphWidth / 2), endY + 4, { align: 'center' });
            doc.text(fmtTime(endDate), endX, endY + 4, { align: 'right' });

            const drawSeries = (key: string, color: [number, number, number], isRight: boolean) => {
                doc.setDrawColor(...color);
                doc.setLineWidth(0.2);

                const step = Math.ceil(dataPoints.length / 1000);

                for (let i = 0; i < dataPoints.length - step; i += step) {
                    const p1 = dataPoints[i];
                    const p2 = dataPoints[i + step];
                    if (!p1 || !p2) continue;

                    const x1 = getX(new Date(p1.timestamp).getTime());
                    const x2 = getX(new Date(p2.timestamp).getTime());
                    const v1 = p1[key] || 0;
                    const v2 = p2[key] || 0;
                    const y1 = isRight ? getYRight(v1) : getYLeft(v1);
                    const y2 = isRight ? getYRight(v2) : getYLeft(v2);

                    doc.line(x1, y1, x2, y2);
                }
            };

            drawSeries('flow', [241, 108, 32], false);
            drawSeries('pressure', [19, 149, 186], false);
            drawSeries('volume', [242, 183, 5], true);
            drawSeries('cement', [13, 60, 85], true);

            // Final Value Labels
            const lastPoint = dataPoints[dataPoints.length - 1];
            if (lastPoint) {
                const lastX = getX(new Date(lastPoint.timestamp).getTime());
                const lastCem = lastPoint.cement || 0;
                let yCem = getYRight(lastCem);

                const lastVol = lastPoint.volume || 0;
                let yVol = getYRight(lastVol);

                if (Math.abs(yCem - yVol) < 4) {
                    const mid = (yCem + yVol) / 2;
                    if (yCem <= yVol) {
                        yCem = mid - 2;
                        yVol = mid + 2;
                    } else {
                        yCem = mid + 2;
                        yVol = mid - 2;
                    }
                }

                doc.setTextColor(13, 60, 85);
                doc.setFont(doc.getFont().fontName, 'bold');
                doc.setFontSize(10);
                doc.text(`${lastCem.toFixed(0) }`, lastX + 2, yCem, { align: 'left' });

                doc.setTextColor(242, 183, 5);
                doc.setFont(doc.getFont().fontName, 'bold');
                doc.setFontSize(10);
                doc.text(`${lastVol.toFixed(0)}`, lastX + 2, yVol, { align: 'left' });
            }
            doc.setFontSize(6);
            doc.setFont(doc.getFont().fontName, 'cursive');
            const legendY = startY + 2;
            const legendX = startX + 2;
            const items = [
                { l: 'Flow', c: [241, 108, 32] },
                { l: 'Pressure', c: [19, 149, 186] },
                { l: 'Volume', c: [242, 183, 5] },
                { l: 'Cement', c: [13, 60, 85] }
            ];

            items.forEach((item, idx) => {
                const lx = legendX + (idx * 25);
                doc.setFillColor(...(item.c as [number, number, number]));
                doc.rect(lx, legendY, 2, 2, 'F');
                doc.setTextColor(50);
                doc.text(item.l, lx + 3.5, legendY + 1.7);
            });

            finalY += graphHeight + 15;
        });

        return finalY;
    }

    static AddEventsSection(data: any, doc: any, finalY: number) {
        const events = data.events;

        if (events && events.length > 0) {
            doc.setFontSize(14);
            doc.text("Events", 14, finalY + 10);
            finalY = this.AddHeaderSubtext(data, doc, finalY + 15);
            finalY += 10;

            autoTable(doc, {
                startY: finalY + 15,
                head: [['Time', 'Event Type', 'Unit', 'C1', 'C2', 'T.Acc', 'T.Add1', 'T.Add2', 'T.Add3', 'T.Add4', 'T.Add5', 'T.Add6', 'PCkWh']],
                body: events.map((e: any) => [
                    e.timestamp ? new Date(e.timestamp).toLocaleString() : '',
                    e.eventType,
                    e.eventUnit,
                    e.totalCementOne,
                    e.totalCementTwo,
                    e.totalAccelerator,
                    e.totalAdditiveOne,
                    e.totalAdditiveTwo,
                    e.totalAdditiveThree,
                    e.totalAdditiveFour,
                    e.totalAdditiveFive,
                    e.totalAdditiveSix,
                    e.powerConskWh
                ]),
                theme: 'striped',
                headStyles: { fillColor: [96, 125, 139] },
                styles: { fontSize: 9 },
                columnStyles: {
                    0: { cellWidth: 19 },
                    1: { cellWidth: 20 },
                    2: { cellWidth: 'auto' },
                    3: { cellWidth: 'auto' },
                    4: { cellWidth: 'auto' },
                    5: { cellWidth: 'auto' },
                    6: { cellWidth: 'auto' },
                    7: { cellWidth: 'auto' },
                    8: { cellWidth: 'auto' },
                    9: { cellWidth: 'auto' },
                    10: { cellWidth: 'auto' },
                    11: { cellWidth: 'auto' },
                    12: { cellWidth: 'auto' }

                }
            });
            finalY = (doc as any).lastAutoTable.finalY;
        }
        return finalY;
    }

    static _triggerDownload(buffer: any, filename: string, mimeType: string) {
        const blob = new Blob([buffer], { type: mimeType });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(link.href);
    }

    static _pumpIdToRgb(pumpId: number) {
        const hue = (pumpId * 60) % 360;
        const s = 0.7;
        const l = 0.5;

        const c = (1 - Math.abs(2 * l - 1)) * s;
        const x = c * (1 - Math.abs((hue / 60) % 2 - 1));
        const m = l - c / 2;

        let r = 0, g = 0, b = 0;

        if (0 <= hue && hue < 60) { r = c; g = x; b = 0; }
        else if (60 <= hue && hue < 120) { r = x; g = c; b = 0; }
        else if (120 <= hue && hue < 180) { r = 0; g = c; b = x; }
        else if (180 <= hue && hue < 240) { r = 0; g = x; b = c; }
        else if (240 <= hue && hue < 300) { r = x; g = 0; b = c; }
        else if (300 <= hue && hue < 360) { r = c; g = 0; b = x; }

        return [
            Math.round((r + m) * 255),
            Math.round((g + m) * 255),
            Math.round((b + m) * 255)
        ];
    }

    static _recipeIdToRgb(recipeId: number) {
        const hue = (recipeId * 137.508) % 360;
        const s = 0.7;
        const l = 0.5;

        const c = (1 - Math.abs(2 * l - 1)) * s;
        const x = c * (1 - Math.abs((hue / 60) % 2 - 1));
        const m = l - c / 2;

        let r = 0, g = 0, b = 0;

        if (0 <= hue && hue < 60) { r = c; g = x; b = 0; }
        else if (60 <= hue && hue < 120) { r = x; g = c; b = 0; }
        else if (120 <= hue && hue < 180) { r = 0; g = c; b = x; }
        else if (180 <= hue && hue < 240) { r = 0; g = x; b = c; }
        else if (240 <= hue && hue < 300) { r = x; g = 0; b = c; }
        else if (300 <= hue && hue < 360) { r = c; g = 0; b = x; }

        return [
            Math.round((r + m) * 255),
            Math.round((g + m) * 255),
            Math.round((b + m) * 255)
        ];
    }

    static _formatDurationFromDays(totalDays: number) {
        if (!totalDays || isNaN(totalDays)) return "00:00:00";
        let totalSeconds = Math.round(totalDays * 86400);
        const hours = Math.floor(totalSeconds / 3600);
        totalSeconds %= 3600;
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }

    static _calculateTotalTimeFromMs = (totalTimeMs: number) => {
        const hours = Math.floor(totalTimeMs / 3600000).toString().padStart(2, '0');
        const mins = Math.floor((totalTimeMs % 3600000) / 60000).toString().padStart(2, '0');
        const secs = Math.floor((totalTimeMs % 60000) / 1000).toString().padStart(2, '0');
        return `${hours}:${mins}:${secs}`;
    };

    private static DrawScatterChart(
        doc: any,
        items: { holeNo: number; cement: number; pressure: number }[],
        startX: number, startY: number,
        chartW: number, chartH: number
    ): number {
        if (!items.length) return startY + chartH;

        const PRESSURE_LOW  = 10;
        const PRESSURE_HIGH = 25;
        const COL_LOW:  [number, number, number] = [229, 57,  53];
        const COL_MED:  [number, number, number] = [240, 179, 35];
        const COL_HIGH: [number, number, number] = [76,  175, 80];
        const AXIS_COL: [number, number, number] = [75,  94,  109];
        const GRID_COL: [number, number, number] = [220, 224, 228];

        const legendH = 12;
        const padL = 6, padR = 4, padT = legendH + 4, padB = 16;
        const plotX = startX + padL;
        const plotY = startY + padT;
        const plotW = chartW - padL - padR;
        const plotH = chartH - padT - padB;

        const cements   = items.map(d => d.cement);
        const pressures = items.map(d => d.pressure);
        const minC = Math.min(...cements), maxC = Math.max(...cements);
        const maxP = Math.max(...pressures, PRESSURE_HIGH + 5);
        const cRange = maxC - minC || 1;

        const toX = (c: number) => plotX + ((c - minC) / cRange) * plotW;
        const toY = (p: number) => plotY + plotH - (p / maxP) * plotH;

        // White background for entire chart area
        doc.setFillColor(255, 255, 255);
        doc.rect(startX, startY, chartW, chartH, 'F');

        // Legend above the plot — horizontal row centred over the chart
        const zones: [string, [number,number,number]][] = [
            [`High (>=${PRESSURE_HIGH} bar)`, COL_HIGH],
            [`Moderate (${PRESSURE_LOW}-${PRESSURE_HIGH} bar)`, COL_MED],
            [`Low (<${PRESSURE_LOW} bar)`, COL_LOW],
        ];
        doc.setFontSize(6);
        doc.setFont('helvetica', 'normal');
        const itemW = 58;
        const totalLegendW = zones.length * itemW;
        let legendX = startX + (chartW - totalLegendW) / 2;
        const legendY = startY + 5;
        zones.forEach(([label, col]) => {
            doc.setFillColor(...col);
            doc.circle(legendX + 2, legendY, 2, 'F');
            doc.setTextColor(...AXIS_COL);
            doc.text(label, legendX + 6, legendY + 0.9);
            legendX += itemW;
        });

        // Plot area background
        doc.setFillColor(248, 249, 250);
        doc.rect(plotX, plotY, plotW, plotH, 'F');

        // Horizontal grid lines + Y labels
        doc.setFontSize(5.5);
        doc.setFont('helvetica', 'normal');
        const gridVals = [0, PRESSURE_LOW, PRESSURE_HIGH, Math.round(maxP)];
        gridVals.forEach(val => {
            const gy = toY(val);
            doc.setDrawColor(...GRID_COL);
            doc.setLineWidth(0.2);
            doc.line(plotX, gy, plotX + plotW, gy);
            doc.setTextColor(...AXIS_COL);
            doc.text(String(val), plotX - 1.5, gy + 0.8, { align: 'right' });
        });

        // Threshold lines at zone boundaries
        doc.setDrawColor(229, 57, 53);
        doc.setLineWidth(0.3);
        doc.line(plotX, toY(PRESSURE_LOW), plotX + plotW, toY(PRESSURE_LOW));
        doc.setDrawColor(76, 175, 80);
        doc.line(plotX, toY(PRESSURE_HIGH), plotX + plotW, toY(PRESSURE_HIGH));

        // Scatter dots
        const DOT_R = 1;
        items.forEach(d => {
            const col = d.pressure >= PRESSURE_HIGH ? COL_HIGH : d.pressure >= PRESSURE_LOW ? COL_MED : COL_LOW;
            doc.setFillColor(...col);
            doc.circle(toX(d.cement), toY(d.pressure), DOT_R, 'F');
        });

        // Axes
        doc.setDrawColor(...AXIS_COL);
        doc.setLineWidth(0.4);
        doc.line(plotX, plotY, plotX, plotY + plotH);
        doc.line(plotX, plotY + plotH, plotX + plotW, plotY + plotH);

        // X-axis labels
        doc.setFontSize(5);
        doc.setTextColor(...AXIS_COL);
        const xSteps = Math.min(items.length, 6);
        for (let i = 0; i <= xSteps; i++) {
            const val = minC + (cRange / xSteps) * i;
            doc.text(Math.round(val).toLocaleString(), toX(val), plotY + plotH + 4, { align: 'center' });
        }

        // X-axis title
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...AXIS_COL);
        doc.text('Total Cement (kg)', startX + chartW / 2, startY + chartH + 1, { align: 'center' });

        doc.setLineWidth(0.2);
        doc.setDrawColor(0, 0, 0);
        doc.setTextColor(0, 0, 0);

        return startY + chartH + 6;
    }
}